// Which company the app presents itself as: header colour, logo and wordmark. DOKA and the
// Umdasch Group are the same product to the same backend - only the chrome differs, depending on
// where the user entered from (see chat: "Abhängig vom Einstieg hat der Kunde den Wunsch,
// unterschiedliche Unternehmen abzubilden (DOKA vs. Umdasch Group)").
//
// Resolution order, first hit wins:
//   1. ?brand=umdasch in the URL     - THE entry-point switch; also remembered (see below)
//   2. localStorage "pih.brand"      - what the user entered with last time
//   3. VITE_BRAND from .env          - the deployment's own default
//   4. "umdasch" (the PIH presents itself as Umdasch Group, like the group's Identity login -
//      reported: "Der Header soll so blau werden ... mit dem Umdasch Logo oben links")
//
// Unlike ?apitest (sessionStorage, a debug aid that should die with the tab), the brand is kept in
// localStorage: a user who came in via an Umdasch entry point should still see Umdasch chrome on
// the next visit, without that entry point's query string (see screenshot note "Sprache, Company
// speichern"). ?brand= with an empty value clears the stored choice again.
//
// Every individual value can additionally be overridden from .env (VITE_BRAND_*), so a new
// company - or a corrected colour/logo - needs a config change, not a code change.

export interface BrandDefinition {
    key: string;
    /** Company name - shown as a plain wordmark whenever no logo image is configured. */
    name: string;
    /** Header bar background colour. */
    headerBackground: string;
    /** Colour for everything sitting IN that bar: app title, Merkliste, language select. */
    headerForeground: string;
    /**
     * The brand's accent for the page itself: buttons, the top heading of a view, links, active
     * tabs, the selected tree row (see applyBrandColors). DOKA blue for DOKA; for Umdasch Group the
     * header's own dark blue (reported: "Schriftfarbe auf das dunkle Umdasch-Blau umstellen ... das
     * gilt auch für die Buttons und weiteren Texte").
     */
    primary: string;
    /** Logo image; relative paths resolve against the app's base URL. Empty -> `name` wordmark. */
    logoUrl: string;
    /** Rendered logo height in px inside the header bar. */
    logoHeight: number;
    /** Transparent margin the logo file carries on its left, in px at `logoHeight` - subtracted
     * so the visible mark, not the image box, starts on the header's 24px content edge. */
    logoInset: number;
    /**
     * Whether this brand paints its OWN header bar (BrandHeaderBar) over the one the private
     * @uag/react-core BaseLayout renders. False for DOKA: that yellow bar with the DOKA logo IS
     * the blueprint's own chrome, and there is no prop to recolour or re-logo it - so a
     * non-DOKA brand can only be done by covering it.
     */
    overridesHeader: boolean;
}

const DEFAULT_BRAND_KEY = 'umdasch';

export const BRANDS: Record<string, BrandDefinition> = {
    // The blueprint's own look - values mirrored here (rather than left blank) because the header
    // OVERLAYS (HeaderBrand/HeaderWishlist/LanguageSwitch) read headerForeground for their text
    // colour, and on the yellow DOKA bar that has always been DOKA blue.
    doka: {
        key: 'doka',
        name: 'Doka',
        headerBackground: '#FEDC00',
        headerForeground: '#004588',
        primary: '#004588',
        logoUrl: '',
        logoHeight: 28,
        logoInset: 0,
        overridesHeader: false
    },
    // Umdasch Group as on the group's Identity login: dark corporate blue (#0C2340, sampled from
    // that page) with the white/lime logo (public/umdasch_logo.svg, supplied by the customer).
    // The SVG carries generous transparent margins (~23% top/bottom, ~9% left), hence the box
    // height of 84px for a visible mark of ~46px, and the inset.
    umdasch: {
        key: 'umdasch',
        name: 'umdasch group',
        headerBackground: '#0C2340',
        headerForeground: '#FFFFFF',
        primary: '#0C2340',
        logoUrl: 'umdasch_logo.svg',
        logoHeight: 84,
        logoInset: 18,
        overridesHeader: true
    }
};

const STORAGE_KEY = 'pih.brand';

const env = (name: keyof ImportMetaEnv): string => (import.meta.env[name] ?? '').trim();

const readBrandKey = (): string => {
    const fromEnv = env('VITE_BRAND').toLowerCase();

    if (typeof window === 'undefined') {
        return fromEnv || DEFAULT_BRAND_KEY;
    }

    const fromQuery = new URLSearchParams(window.location.search).get('brand');
    if (fromQuery !== null) {
        const requested = fromQuery.trim().toLowerCase();
        // A present-but-empty ?brand= means "forget what I picked", not "no parameter given".
        if (!requested) {
            window.localStorage.removeItem(STORAGE_KEY);
            return fromEnv || DEFAULT_BRAND_KEY;
        }
        if (BRANDS[requested]) {
            window.localStorage.setItem(STORAGE_KEY, requested);
            return requested;
        }
        // eslint-disable-next-line no-console
        console.warn(`brand: unknown value "${requested}" - known brands are ${Object.keys(BRANDS).join(', ')}.`);
    }

    const stored = window.localStorage.getItem(STORAGE_KEY)?.toLowerCase();
    if (stored && BRANDS[stored]) {
        return stored;
    }

    return fromEnv || DEFAULT_BRAND_KEY;
};

const base = BRANDS[readBrandKey()] ?? BRANDS[DEFAULT_BRAND_KEY];

const logoHeight = Number(env('VITE_BRAND_LOGO_HEIGHT'));

/**
 * The active brand: the resolved brand definition with any per-value .env override applied on top.
 * Read at module load, like authMode/apiTest - the brand cannot change without a full page load.
 */
export const brand: BrandDefinition = {
    ...base,
    name: env('VITE_BRAND_NAME') || base.name,
    headerBackground: env('VITE_BRAND_HEADER_BACKGROUND') || base.headerBackground,
    headerForeground: env('VITE_BRAND_HEADER_FOREGROUND') || base.headerForeground,
    primary: env('VITE_BRAND_PRIMARY') || base.primary,
    logoUrl: env('VITE_BRAND_LOGO_URL') || base.logoUrl,
    logoHeight: Number.isFinite(logoHeight) && logoHeight > 0 ? logoHeight : base.logoHeight,
    // An explicitly configured colour or logo only takes effect if something actually paints them,
    // so setting either one on the DOKA default implies "yes, override the blueprint's bar".
    overridesHeader:
        base.overridesHeader ||
        Boolean(env('VITE_BRAND_HEADER_BACKGROUND') || env('VITE_BRAND_LOGO_URL') || env('VITE_BRAND_NAME'))
};

/** Colour for the header overlays (app title, Merkliste, language select) - brand-dependent. */
export const headerForeground = brand.headerForeground;

const hexToRgb = (hex: string): [number, number, number] => {
    const n = parseInt(hex.replace('#', '').slice(0, 6), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const mix = (hex: string, toward: [number, number, number], amount: number) =>
    `#${hexToRgb(hex)
        .map((c, i) => Math.round(c + ((toward[i] - c) * amount)).toString(16).padStart(2, '0'))
        .join('')}`;

/**
 * Ten shades of the brand's primary for Mantine's colour scale (lightest first), the primary
 * itself at index 6 - so filled buttons, light badges, the selected tree row and every other
 * primary-coloured Mantine part follow the brand instead of the theme's built-in blue.
 */
export const brandPalette = (): string[] => [
    ...[0.94, 0.86, 0.72, 0.56, 0.4, 0.2].map((amount) => mix(brand.primary, [255, 255, 255], amount)),
    brand.primary,
    ...[0.15, 0.3, 0.45].map((amount) => mix(brand.primary, [0, 0, 0], amount))
];

/** CSS custom property carrying the brand's primary, for App.css and inline styles. */
export const PRIMARY_VAR = '--pih-primary';

/**
 * The neutral half of the palette. With the brand's primary these are the ONLY colours the UI uses
 * (reported: "mit einigen Grundfarben sauber sein: Dunkelblau, Grau, Schwarz (Text)") - besides
 * red for errors. Everything else (Mantine's text/dimmed/border/anchor colours, the theme's blue
 * scale @uag/doka-design paints with) is pointed at these in applyBrandColors.
 */
export const NEUTRALS = {
    /** Body text. */
    text: '#1A1A1A',
    /** Secondary text: counts, hints, captions. */
    muted: '#5F6670',
    /** Lines and outlines: inputs, cards, image frames, table rules. */
    border: '#D5D9DE',
    /** Quiet fills: chips, hover rows, selected tree row, image backdrops. */
    surface: '#F3F4F6',
    /** Hover on a quiet fill. */
    surfaceHover: '#E8EAED'
} as const;

/**
 * Publishes the brand's primary as CSS variables on <html>: our own `--pih-primary` (App.css and
 * inline styles read it), and Mantine's primary-colour variables too - @uag/doka-design may set
 * those from its own DOKA theme, and an inline value on <html> wins over any stylesheet rule for
 * them. Called once at start-up (main.tsx).
 */
export const applyBrandColors = () => {
    const palette = brandPalette();
    const root = document.documentElement.style;
    const set = (name: string, value: string) => root.setProperty(name, value);
    // Our own tokens (App.css and inline styles read these).
    set(PRIMARY_VAR, brand.primary);
    set('--pih-primary-hover', palette[7]);
    set('--pih-primary-light', palette[0]);
    set('--pih-text', NEUTRALS.text);
    set('--pih-muted', NEUTRALS.muted);
    set('--pih-border', NEUTRALS.border);
    set('--pih-surface', NEUTRALS.surface);
    set('--pih-surface-hover', NEUTRALS.surfaceHover);
    // Mantine's own colour variables, pointed at the same tokens: text, dimmed text, links, default
    // borders - and the primary AND the blue scale, which @uag/doka-design's DOKA theme paints
    // light backgrounds and accents with (the light blue of the selected tree row, for one).
    set('--mantine-color-text', NEUTRALS.text);
    set('--mantine-color-dimmed', NEUTRALS.muted);
    set('--mantine-color-anchor', brand.primary);
    set('--mantine-color-default-border', NEUTRALS.border);
    for (const scale of ['primary-color', 'color-blue']) {
        set(`--mantine-${scale}-filled`, brand.primary);
        set(`--mantine-${scale}-filled-hover`, palette[7]);
        set(`--mantine-${scale}-light`, NEUTRALS.surface);
        set(`--mantine-${scale}-light-hover`, NEUTRALS.surfaceHover);
        set(`--mantine-${scale}-light-color`, brand.primary);
        set(`--mantine-${scale}-outline`, brand.primary);
        set(`--mantine-${scale}-outline-hover`, NEUTRALS.surface);
        palette.forEach((shade, i) => set(`--mantine-${scale}-${i}`, shade));
    }
};

if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.info('brand config:', brand);
}
